import React, { useRef, useState } from 'react';

import { useFormContext, useWatch } from 'react-hook-form';

import { Input } from 'components/ui/input';
import { Switch } from 'components/ui/switch';

import {
  EOfferFormatKey,
  IStoreSettingsFormValues
} from '../../../StoreSettings.types';
import {
  ABBREVIATION_SWITCH_CLASSNAME,
  ABBREVIATIONS_V2_TEST_IDS,
  ABBREVIATIONS_V2_TEXTS,
  MIN_DIGITS_WITHOUT_SHORTHAND
} from '../AbbreviationsSettingsV2.consts';
import {
  QuantityFieldPath,
  QuantityFieldValue
} from '../AbbreviationsSettingsV2.types';
import { getQuantityFieldPath } from '../AbbreviationsSettingsV2.utils';

interface IShowFullNumberFieldProps {
  sectionKey: EOfferFormatKey;
  onCommit: (path: QuantityFieldPath, value: QuantityFieldValue) => void;
}

export const ShowFullNumberField = ({
  sectionKey,
  onCommit
}: IShowFullNumberFieldProps): React.ReactElement => {
  const { control, getValues } = useFormContext<IStoreSettingsFormValues>();
  const fieldPath = getQuantityFieldPath(sectionKey, 'maxDigitsWithoutShorthand');
  const digitsBasedPath = getQuantityFieldPath(
    sectionKey,
    'digitsBasedAbbreviation'
  );
  const initialDigits = Number(getValues(fieldPath));
  const [isShowingInput, setIsShowingInput] = useState<boolean>(
    () => initialDigits >= MIN_DIGITS_WITHOUT_SHORTHAND
  );
  const lastEnteredDigitsRef = useRef<number>(
    initialDigits >= MIN_DIGITS_WITHOUT_SHORTHAND
      ? initialDigits
      : MIN_DIGITS_WITHOUT_SHORTHAND
  );
  const maxDigits = useWatch({ control, name: fieldPath });

  const handleDigitsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const nextDigits = Number(event.target.value);
    if (nextDigits >= MIN_DIGITS_WITHOUT_SHORTHAND) {
      lastEnteredDigitsRef.current = nextDigits;
    }
    onCommit(fieldPath, nextDigits);
  };

  const handleVisibilityChange = (checked: boolean): void => {
    setIsShowingInput(checked);
    onCommit(fieldPath, checked ? lastEnteredDigitsRef.current : 0);
    onCommit(digitsBasedPath, checked);
  };

  return (
    <div className="flex h-[58px] w-full items-center justify-between rounded-[10px] border border-base-border px-[17px]">
      <span className="text-sm font-medium text-base-foreground">
        {ABBREVIATIONS_V2_TEXTS.SHOW_FULL_NUMBER_LABEL}
      </span>
      <div className="flex items-center gap-4">
        {isShowingInput && (
          <Input
            type="number"
            min={MIN_DIGITS_WITHOUT_SHORTHAND}
            value={String(maxDigits)}
            onChange={handleDigitsChange}
            className="h-9 w-20"
            aria-label={ABBREVIATIONS_V2_TEXTS.MAX_DIGITS_LABEL}
            data-testid={ABBREVIATIONS_V2_TEST_IDS.maxDigitsInput}
          />
        )}
        <Switch
          checked={isShowingInput}
          onCheckedChange={handleVisibilityChange}
          className={ABBREVIATION_SWITCH_CLASSNAME}
          data-testid={ABBREVIATIONS_V2_TEST_IDS.showFullNumber}
        />
      </div>
    </div>
  );
};
